import { useId, type CSSProperties } from "react";

// The "Verified by SkillPatch" seal, shown in the VerifiedBadge explainer modal.

const GREEN = "#22c55e";
const GREEN_L = "#4ade80";

export type Pillar = { key: string; name: string; desc: string; rubric?: boolean };

// Force the dark seal palette even under the light global theme (the app's modals
// are always dark). Mirrors the [data-theme="dark"] --seal-* tokens in index.css.
const SEAL_DARK_VARS = {
  "--seal-line": "#22c55e",
  "--seal-check": "#4ade80",
  "--seal-node-0": "rgba(34,197,94,0.22)",
  "--seal-node-1": "rgba(34,197,94,0.06)",
  "--seal-label": "#cbd5e1",
} as CSSProperties;

// The verification pillars every "Verified by SkillPatch" skill passes — one per real
// pipeline gate (Scan → Security). Intent is not a separate gate: the Security review
// judges each capability against the skill's declared purpose, so it lives in that pillar.
export const VERIFIED_PILLARS: Pillar[] = [
  { key: "structure", name: "Structural review", desc: "The Scan agent validates the skill's structure, format and referenced files." },
  { key: "security", name: "Security review", desc: "An intent-aware, six-dimension safety review — every capability judged against the skill's declared purpose.", rubric: true },
];

// The seal: a central shield ringed by one named circle-node per pillar.
// Colour + glow mirror the security-rubric radar (green = pass) and the structural
// strokes are strong enough to read on light backgrounds too.
// `tone="dark"` forces light labels for the app's always-dark modals.
export function VerifiedSeal({ size = 210, tone = "auto" }: { size?: number; tone?: "auto" | "dark" }) {
  const id = useId().replace(/:/g, "");
  const cx = 110, cy = 108, R = 70;
  const pos = (i: number) => {
    const a = -Math.PI / 2 + (i * 2 * Math.PI) / VERIFIED_PILLARS.length;
    return { a, x: cx + R * Math.cos(a), y: cy + R * Math.sin(a) };
  };
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 220 216"
      className="overflow-visible"
      style={tone === "dark" ? SEAL_DARK_VARS : undefined}
    >
      <defs>
        {/* Soft green glow behind the plot — like the radar's --radar-glow halo */}
        <radialGradient id={`${id}g`} cx="50%" cy="45%" r="58%">
          <stop offset="0%" stopColor={GREEN} stopOpacity={0.28} />
          <stop offset="70%" stopColor={GREEN} stopOpacity={0.04} />
          <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
        </radialGradient>
        <linearGradient id={`${id}sh`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={GREEN_L} />
          <stop offset="100%" stopColor="#16a34a" />
        </linearGradient>
        <linearGradient id={`${id}node`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: "var(--seal-node-0)" }} />
          <stop offset="100%" style={{ stopColor: "var(--seal-node-1)" }} />
        </linearGradient>
        {/* Same glow recipe as the radar polygon (feGaussianBlur → merge) */}
        <filter id={`${id}f`} x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>
      <circle cx={cx} cy={cy} r={R} fill={`url(#${id}g)`} />
      <circle cx={cx} cy={cy} r={R} fill="none" style={{ stroke: "var(--seal-line)" }} strokeOpacity={0.5} strokeWidth={1.25} />
      {VERIFIED_PILLARS.map((_, i) => {
        const { x, y } = pos(i);
        return <line key={i} x1={cx} y1={cy} x2={x} y2={y} style={{ stroke: "var(--seal-line)" }} strokeOpacity={0.35} strokeWidth={1.25} />;
      })}
      {/* Central shield — gradient fill + glow, checkmark centred on its body */}
      <g filter={`url(#${id}f)`}>
        <path d="M110 78 l18 6 v12 c0 13 -9 20 -18 24 c-9 -4 -18 -11 -18 -24 v-12 z" fill={`url(#${id}sh)`} />
        <path d="M101 97 l6 6 l13 -14" fill="none" stroke="#06210f" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
      </g>
      {VERIFIED_PILLARS.map((p, i) => {
        const { a, x, y } = pos(i);
        const c = Math.cos(a);
        // Top node → label above; side nodes → label beside.
        const isTop = Math.abs(c) <= 0.3;
        const anchor = isTop ? "middle" : c > 0 ? "start" : "end";
        const lx = isTop ? x : x + (c > 0 ? 22 : -22);
        const ly = isTop ? y - 26 : y;
        return (
          <g key={p.key}>
            <circle cx={x} cy={y} r={16} fill={`url(#${id}node)`} style={{ stroke: "var(--seal-line)" }} strokeWidth={1.75} filter={`url(#${id}f)`} />
            <path d={`M${x - 4.5} ${y} l3.5 3.5 l6.5 -7.5`} fill="none" style={{ stroke: "var(--seal-check)" }} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" />
            <text x={lx} y={ly} textAnchor={anchor} dominantBaseline="middle" className="text-[11px] font-semibold" style={{ fill: "var(--seal-label)" }}>
              {p.name}
            </text>
          </g>
        );
      })}
    </svg>
  );
}
